import type { Patient } from '@medplum/fhirtypes';
import { useMedplum } from '@medplum/react-hooks';
import {
  Badge,
  Container,
  Paper,
  Stack,
  Table,
  Tabs,
  Text,
  Title,
} from '@mantine/core';
import { useDebouncedValue } from '@mantine/hooks';
import {
  IconUserCheck,
  IconUserOff,
  IconUsers,
} from '@tabler/icons-react';
import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Loading } from '../../components/Loading';
import { useEffectiveDisplayUserRole } from '../../lib/displayUserRole';
import { SearchInput } from '../../components/SearchInput';
import { UnifyAdminClientsPanel } from './unify-admin-clients';

/** Mock supplemental data for active clients (by patient index for demo). */
const MOCK_ACTIVE_SUPPLEMENT: Array<{
  numberOfServices: number;
  serviceType: 'Individual' | 'Couples' | 'Family';
  nextAppointment: string;
  paymentType: 'Cash' | 'Insurance';
}> = [
  { numberOfServices: 1, serviceType: 'Individual', nextAppointment: '2025-03-15', paymentType: 'Insurance' },
  { numberOfServices: 2, serviceType: 'Couples', nextAppointment: '2025-03-18', paymentType: 'Cash' },
  { numberOfServices: 1, serviceType: 'Family', nextAppointment: '2025-03-20', paymentType: 'Insurance' },
  { numberOfServices: 1, serviceType: 'Individual', nextAppointment: '--', paymentType: 'Insurance' },
  { numberOfServices: 2, serviceType: 'Individual', nextAppointment: '2025-03-22', paymentType: 'Cash' },
];

/** Mock supplemental data for inactive clients (by patient index for demo). lengthOfCareMonths: whole months in care; 0 = "< 1 month". */
const MOCK_INACTIVE_SUPPLEMENT: Array<{
  firstAppointment: string;
  mostRecentAppointment: string;
  dischargeDate: string;
  lengthOfCareMonths: number;
  servicesRendered: string;
}> = [
  { firstAppointment: '2024-09-01', mostRecentAppointment: '2025-02-28', dischargeDate: '2025-03-01', lengthOfCareMonths: 6, servicesRendered: 'Individual Therapy, Group Therapy' },
  { firstAppointment: '2024-12-15', mostRecentAppointment: '2025-02-15', dischargeDate: '2025-02-20', lengthOfCareMonths: 2, servicesRendered: 'Couples Therapy' },
  { firstAppointment: '2025-01-08', mostRecentAppointment: '2025-01-10', dischargeDate: '2025-01-15', lengthOfCareMonths: 0, servicesRendered: 'Group Therapy' },
  { firstAppointment: '2025-01-15', mostRecentAppointment: '--', dischargeDate: '2025-02-01', lengthOfCareMonths: 1, servicesRendered: 'IOP, Individual Therapy' },
  { firstAppointment: '2023-03-10', mostRecentAppointment: '2025-03-01', dischargeDate: '2025-03-05', lengthOfCareMonths: 24, servicesRendered: 'Individual Therapy' },
];

interface InactiveRowDisplay {
  id: string;
  name: string;
  email?: string;
  phone?: string;
  firstAppointment: string;
  mostRecentAppointment: string;
  dischargeDate: string;
  lengthOfCareMonths: number;
  servicesRendered: string;
}

function formatDisplayDate(dateStr: string): string {
  if (!dateStr || dateStr === '--') return dateStr;
  try {
    return new Date(dateStr).toLocaleDateString('en-US');
  } catch {
    return dateStr;
  }
}

/** Format length of care for inactive tab: "< 1 month" | "1 month" | "N months". */
function formatLengthOfCare(months: number): string {
  if (months < 1) return '< 1 month';
  if (months === 1) return '1 month';
  return `${months} months`;
}

/**
 * Format a FHIR HumanName to display string
 */
function formatPatientName(patient: Patient): string {
  const name = patient.name?.[0];
  if (!name) return 'Unknown';

  const given = name.given?.join(' ') || '';
  const family = name.family || '';
  return `${given} ${family}`.trim() || 'Unknown';
}

/**
 * Format date of birth for display
 */
function formatBirthDate(date: string | undefined): string {
  if (!date) return '--';
  try {
    return new Date(date).toLocaleDateString('en-US');
  } catch {
    return date;
  }
}

/**
 * Get status badge color
 */
function getStatusColor(active: boolean | undefined): string {
  return active === false ? 'gray' : 'green';
}

function patientTelecom(patient: Patient, system: 'email' | 'phone'): string | undefined {
  return patient.telecom?.find((t) => t.system === system)?.value?.trim() || undefined;
}

/**
 * Patient list page
 * Displays all patients with search and filtering
 */
export function PatientListPage(): JSX.Element {
  const displayUserRole = useEffectiveDisplayUserRole();
  const isUnifyAdmin = displayUserRole === 'Unify Admin';
  const medplum = useMedplum();
  const [patients, setPatients] = useState<Patient[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [debouncedSearch] = useDebouncedValue(search, 300);
  const [activeTab, setActiveTab] = useState<string | null>('all');

  const activePatients = useMemo(
    () => patients.filter((p) => p.active !== false),
    [patients]
  );
  const inactivePatients = useMemo(
    () => patients.filter((p) => p.active === false),
    [patients]
  );
  const inactiveDisplayRows = useMemo((): InactiveRowDisplay[] => {
    return inactivePatients.map((p, i) => {
      const sup = MOCK_INACTIVE_SUPPLEMENT[i % MOCK_INACTIVE_SUPPLEMENT.length];
      return {
        id: p.id!,
        name: formatPatientName(p),
        email: patientTelecom(p, 'email'),
        phone: patientTelecom(p, 'phone'),
        firstAppointment: sup.firstAppointment,
        mostRecentAppointment: sup.mostRecentAppointment,
        dischargeDate: sup.dischargeDate,
        lengthOfCareMonths: sup.lengthOfCareMonths,
        servicesRendered: sup.servicesRendered,
      };
    });
  }, [inactivePatients]);

  useEffect(() => {
    if (isUnifyAdmin) {
      return;
    }

    setLoading(true);

    const searchParams: Record<string, string> = {
      _count: '50',
      _sort: '-_lastUpdated',
    };

    if (debouncedSearch) {
      searchParams.name = debouncedSearch;
    }

    medplum
      .searchResources('Patient', searchParams)
      .then((results) => {
        setPatients(results);
      })
      .catch(() => {
        // Handled by empty state UI
      })
      .finally(() => {
        setLoading(false);
      });
  }, [isUnifyAdmin, medplum, debouncedSearch]);

  function renderAllRows(list: Patient[]) {
    return list.map((patient) => (
      <Table.Tr key={patient.id}>
        <Table.Td>
          <Text
            component={Link}
            to={`/dashboard/patients/${patient.id}`}
            fw={500}
            c="gold.6"
            style={{ textDecoration: 'none' }}
          >
            {formatPatientName(patient)}
          </Text>
        </Table.Td>
        <Table.Td>{formatBirthDate(patient.birthDate)}</Table.Td>
        <Table.Td>{patient.gender || '--'}</Table.Td>
        <Table.Td>
          <Text size="sm" c={patientTelecom(patient, 'email') ? undefined : 'dimmed'}>
            {patientTelecom(patient, 'email') ?? '--'}
          </Text>
        </Table.Td>
        <Table.Td>
          <Text size="sm" c={patientTelecom(patient, 'phone') ? undefined : 'dimmed'}>
            {patientTelecom(patient, 'phone') ?? '--'}
          </Text>
        </Table.Td>
        <Table.Td>
          <Badge color={getStatusColor(patient.active)} variant="light">
            {patient.active === false ? 'Inactive' : 'Active'}
          </Badge>
        </Table.Td>
        <Table.Td>
          <Text
            component={Link}
            to={`/dashboard/patients/${patient.id}`}
            size="sm"
            c="gold.6"
          >
            View
          </Text>
        </Table.Td>
      </Table.Tr>
    ));
  }

  function renderActiveRows(list: Patient[]) {
    return list.map((patient, index) => {
      const sup = MOCK_ACTIVE_SUPPLEMENT[index % MOCK_ACTIVE_SUPPLEMENT.length];
      return (
        <Table.Tr key={patient.id}>
          <Table.Td>
            <Text
              component={Link}
              to={`/dashboard/patients/${patient.id}`}
              fw={500}
              c="gold.6"
              style={{ textDecoration: 'none' }}
            >
              {formatPatientName(patient)}
            </Text>
          </Table.Td>
          <Table.Td>{sup.numberOfServices}</Table.Td>
          <Table.Td>{sup.serviceType}</Table.Td>
          <Table.Td>
            <Text size="sm" c={patientTelecom(patient, 'email') ? undefined : 'dimmed'}>
              {patientTelecom(patient, 'email') ?? '--'}
            </Text>
          </Table.Td>
          <Table.Td>
            <Text size="sm" c={patientTelecom(patient, 'phone') ? undefined : 'dimmed'}>
              {patientTelecom(patient, 'phone') ?? '--'}
            </Text>
          </Table.Td>
          <Table.Td>{patient.gender || '--'}</Table.Td>
          <Table.Td>{formatDisplayDate(sup.nextAppointment)}</Table.Td>
          <Table.Td>{sup.paymentType}</Table.Td>
        </Table.Tr>
      );
    });
  }

  function renderInactiveRows(rows: InactiveRowDisplay[]) {
    return rows.map((row) => (
      <Table.Tr key={row.id}>
        <Table.Td>
          <Text
            component={Link}
            to={`/dashboard/patients/${row.id}`}
            fw={500}
            c="gold.6"
            style={{ textDecoration: 'none' }}
          >
            {row.name}
          </Text>
        </Table.Td>
        <Table.Td>
          <Text size="sm" c={row.email ? undefined : 'dimmed'}>
            {row.email ?? '--'}
          </Text>
        </Table.Td>
        <Table.Td>
          <Text size="sm" c={row.phone ? undefined : 'dimmed'}>
            {row.phone ?? '--'}
          </Text>
        </Table.Td>
        <Table.Td>{formatDisplayDate(row.firstAppointment)}</Table.Td>
        <Table.Td>{formatDisplayDate(row.mostRecentAppointment)}</Table.Td>
        <Table.Td>{formatDisplayDate(row.dischargeDate)}</Table.Td>
        <Table.Td>{row.servicesRendered}</Table.Td>
        <Table.Td>{formatLengthOfCare(row.lengthOfCareMonths)}</Table.Td>
      </Table.Tr>
    ));
  }

  if (isUnifyAdmin) {
    return <UnifyAdminClientsPanel />;
  }

  return (
    <Container size="xl" py="xl">
      <Stack gap="lg">
        <div>
          <Title order={2}>Clients</Title>
          <Text c="dimmed" size="sm">
            Manage client records and clinical information
          </Text>
        </div>

        <Tabs value={activeTab} onChange={setActiveTab}>
          <Tabs.List styles={{ list: { borderBottom: 'none' } }}>
            <Tabs.Tab value="all" leftSection={<IconUsers size={16} />} style={{ fontWeight: 400 }}>
              All
            </Tabs.Tab>
            <Tabs.Tab value="active" leftSection={<IconUserCheck size={16} />} style={{ fontWeight: 400 }}>
              Active
            </Tabs.Tab>
            <Tabs.Tab value="inactive" leftSection={<IconUserOff size={16} />} style={{ fontWeight: 400 }}>
              Inactive
            </Tabs.Tab>
          </Tabs.List>

          <Tabs.Panel value="all" mt="md">
            <Stack gap="md">
              <SearchInput
                placeholder="Search clients by name..."
                value={search}
                onChange={(e) => setSearch(e.currentTarget.value)}
                style={{ maxWidth: 400 }}
              />

              <Paper p="lg" withBorder>
                {loading ? (
                  <Loading fullScreen={false} />
                ) : patients.length === 0 ? (
                  <Text c="dimmed" ta="center" py="xl">
                    {debouncedSearch
                      ? 'No clients found matching your search'
                      : 'No clients found. Add your first client to get started.'}
                  </Text>
                ) : (
                  <Table highlightOnHover>
                    <Table.Thead>
                      <Table.Tr>
                        <Table.Th><Text size="sm" fw={600}>Name</Text></Table.Th>
                        <Table.Th><Text size="sm" fw={600}>Date of Birth</Text></Table.Th>
                        <Table.Th><Text size="sm" fw={600}>Gender</Text></Table.Th>
                        <Table.Th><Text size="sm" fw={600}>Email</Text></Table.Th>
                        <Table.Th><Text size="sm" fw={600}>Phone</Text></Table.Th>
                        <Table.Th><Text size="sm" fw={600}>Status</Text></Table.Th>
                        <Table.Th><Text size="sm" fw={600}>Actions</Text></Table.Th>
                      </Table.Tr>
                    </Table.Thead>
                    <Table.Tbody>{renderAllRows(patients)}</Table.Tbody>
                  </Table>
                )}

                <Text size="xs" c="dimmed" mt="md">
                  Showing {patients.length} client(s)
                </Text>
              </Paper>
            </Stack>
          </Tabs.Panel>

          <Tabs.Panel value="active" mt="md">
            <Stack gap="md">
              <SearchInput
                placeholder="Search clients by name..."
                value={search}
                onChange={(e) => setSearch(e.currentTarget.value)}
                style={{ maxWidth: 400 }}
              />

              <Paper p="lg" withBorder>
                {loading ? (
                  <Loading fullScreen={false} />
                ) : activePatients.length === 0 ? (
                  <Text c="dimmed" ta="center" py="xl">
                    No active clients.
                  </Text>
                ) : (
                  <Table highlightOnHover>
                    <Table.Thead>
                      <Table.Tr>
                        <Table.Th><Text size="sm" fw={600}>Name</Text></Table.Th>
                        <Table.Th><Text size="sm" fw={600}>Number of services</Text></Table.Th>
                        <Table.Th><Text size="sm" fw={600}>Service type</Text></Table.Th>
                        <Table.Th><Text size="sm" fw={600}>Email</Text></Table.Th>
                        <Table.Th><Text size="sm" fw={600}>Phone</Text></Table.Th>
                        <Table.Th><Text size="sm" fw={600}>Gender</Text></Table.Th>
                        <Table.Th><Text size="sm" fw={600}>Next appointment</Text></Table.Th>
                        <Table.Th><Text size="sm" fw={600}>Payment type</Text></Table.Th>
                      </Table.Tr>
                    </Table.Thead>
                    <Table.Tbody>{renderActiveRows(activePatients)}</Table.Tbody>
                  </Table>
                )}

                <Text size="xs" c="dimmed" mt="md">
                  Showing {activePatients.length} client(s)
                </Text>
              </Paper>
            </Stack>
          </Tabs.Panel>

          <Tabs.Panel value="inactive" mt="md">
            <Stack gap="md">
              <SearchInput
                placeholder="Search clients by name..."
                value={search}
                onChange={(e) => setSearch(e.currentTarget.value)}
                style={{ maxWidth: 400 }}
              />

              <Paper p="lg" withBorder>
                {loading && patients.length === 0 ? (
                  <Loading fullScreen={false} />
                ) : inactiveDisplayRows.length === 0 ? (
                  <Text c="dimmed" ta="center" py="xl">
                    No inactive clients.
                  </Text>
                ) : (
                  <Table highlightOnHover>
                    <Table.Thead>
                      <Table.Tr>
                        <Table.Th><Text size="sm" fw={600}>Name</Text></Table.Th>
                        <Table.Th><Text size="sm" fw={600}>Email</Text></Table.Th>
                        <Table.Th><Text size="sm" fw={600}>Phone</Text></Table.Th>
                        <Table.Th><Text size="sm" fw={600}>First appointment</Text></Table.Th>
                        <Table.Th><Text size="sm" fw={600}>Most recent appointment</Text></Table.Th>
                        <Table.Th><Text size="sm" fw={600}>Discharge date</Text></Table.Th>
                        <Table.Th><Text size="sm" fw={600}>Service(s) Rendered</Text></Table.Th>
                        <Table.Th><Text size="sm" fw={600}>Length of Care</Text></Table.Th>
                      </Table.Tr>
                    </Table.Thead>
                    <Table.Tbody>{renderInactiveRows(inactiveDisplayRows)}</Table.Tbody>
                  </Table>
                )}

                <Text size="xs" c="dimmed" mt="md">
                  Showing {inactiveDisplayRows.length} client(s)
                </Text>
              </Paper>
            </Stack>
          </Tabs.Panel>
        </Tabs>
      </Stack>
    </Container>
  );
}
